import React, { useState } from "react";

function KYCEditModal({ submission, onClose, onSave }) {
  const [verificationStatus, setVerificationStatus] = useState(
    submission.kycVerificationStatus
  );
  const [rejectionReason, setRejectionReason] = useState(
    submission.rejectionReason || ""
  );
  const [expiryDate, setExpiryDate] = useState(
    submission.expiryDate 
      ? (submission.expiryDate.includes('T') 
          ? submission.expiryDate.split('T')[0] 
          : submission.expiryDate.split(' ')[0])
      : ""
  );
  const [saving, setSaving] = useState(false);
  const [showImageModal, setShowImageModal] = useState(null);

  const handleSave = async () => {
    // Validate rejection reason if status is Rejected
    if (verificationStatus === "rejected" && !rejectionReason.trim()) {
      alert("Please provide a reason for rejection.");
      return;
    }

    // Validate expiry date if status is Approved
    if (verificationStatus === "approved" && !expiryDate) {
      alert("Please set an expiry date for approved KYC.");
      return;
    }

    setSaving(true);
    try {
      await onSave(
        submission.id,
        verificationStatus,
        rejectionReason,
        expiryDate || null
      );
      onClose();
    } catch (error) {
      console.error("Error saving:", error);
      // Error message is already shown in handleSave in KYCList
      // Don't close modal on error so user can retry
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    setVerificationStatus(newStatus);
    // Clear rejection reason if status is not Rejected
    if (newStatus !== "rejected") {
      setRejectionReason("");
    }
    // Handle expiry date based on status
    if (newStatus !== "approved") {
      setExpiryDate("");
    } else if (newStatus === "approved" && !expiryDate && submission.expiryDate) {
      // Restore original expiry date if changing to approved
      const originalDate = submission.expiryDate.includes('T') 
        ? submission.expiryDate.split('T')[0] 
        : submission.expiryDate.split(' ')[0];
      setExpiryDate(originalDate);
    }
  };


  // Helper function to check if URL is a PDF
  const isPDF = (url) => {
    if (!url) return false;
    return url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('.pdf?');
  };

  // Helper function to get file name from URL
  const getFileName = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop() || 'document.pdf';
      return decodeURIComponent(fileName);
    } catch {
      // If URL parsing fails, try to extract from string
      const parts = url.split('/');
      const fileName = parts[parts.length - 1] || 'document.pdf';
      return decodeURIComponent(fileName.split('?')[0]);
    }
  };

  const DocumentPreview = ({ src, alt, label }) => {
    const isPdfFile = isPDF(src);
    
    return (
      <div className="mb-3 sm:mb-4">
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        {src ? (
          isPdfFile ? (
            <div className="border border-gray-300 rounded p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm sm:text-base font-medium text-gray-900 break-all">
                      {getFileName(src)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">PDF Document</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 sm:px-4 py-2 bg-blue-600 text-white text-xs sm:text-sm rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Preview
                  </a>
                  <a
                    href={src}
                    download
                    className="px-3 sm:px-4 py-2 bg-gray-600 text-white text-xs sm:text-sm rounded hover:bg-gray-700 transition-colors flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              <img
                src={src}
                alt={alt}
                className="w-full h-40 sm:h-48 object-contain border border-gray-300 rounded cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setShowImageModal(src)}
              />
            </div>
          )
        ) : (
          <div className="w-full h-40 sm:h-48 bg-gray-100 border border-gray-300 rounded flex items-center justify-center text-gray-500 text-xs sm:text-sm">
            No {isPdfFile ? 'document' : 'image'} available
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black/60 flex justify-center items-start sm:items-center z-50 p-0 sm:p-4 overflow-y-auto">
        <div className="bg-white rounded-none sm:rounded-lg w-full sm:w-11/12 max-w-5xl min-h-full sm:min-h-0 sm:max-h-[90vh] overflow-y-auto">
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                KYC Submission Details
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-3xl sm:text-2xl leading-none p-1"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Top Container - Avatar and Text Information */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                {/* Avatar - Left Side */}
                <div className="flex justify-center md:justify-start">
                  <div className="relative">
                    {submission.userImageUrl ? (
                      <img
                        src={submission.userImageUrl}
                        alt="User"
                        className="w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 rounded-full object-cover border-2 sm:border-4 border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => setShowImageModal(submission.userImageUrl)}
                      />
                    ) : (
                      <div className="w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 rounded-full bg-gray-200 border-2 sm:border-4 border-gray-300 flex items-center justify-center text-gray-500 text-xs sm:text-sm">
                        No Photo
                      </div>
                    )}
                  </div>
                </div>

                {/* Text Information - Right Side */}
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                      Full Name
                    </label>
                    <div className="p-2 sm:p-2.5 bg-white border border-gray-300 rounded text-sm sm:text-base text-gray-900 break-words">
                      {submission.fullName}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <div className="p-2 sm:p-2.5 bg-white border border-gray-300 rounded text-sm sm:text-base text-gray-900 break-words">
                      {submission.phoneNumber}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                      Telegram Chat ID
                    </label>
                    <div className="p-2 sm:p-2.5 bg-white border border-gray-300 rounded text-sm sm:text-base text-gray-900 break-words">
                      {submission.telegramChatId}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                      Company Name
                    </label>
                    <div className="p-2 sm:p-2.5 bg-white border border-gray-300 rounded text-sm sm:text-base text-gray-900 break-words">
                      {submission.companyName}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                      OTP Verification Status
                    </label>
                    <div className="p-2 sm:p-2.5 bg-white border border-gray-300 rounded">
                      <span
                        className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                          submission.otpVerificationStatus
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {submission.otpVerificationStatus ? "Verified" : "Not Verified"}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                      Business TIN Number
                    </label>
                    <div className="p-2 sm:p-2.5 bg-white border border-gray-300 rounded text-sm sm:text-base text-gray-900 break-words">
                      {submission.businessTinNumber}
                    </div>
                  </div>

                  {submission.expiryDate && (
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                        KYC Expiry Date
                      </label>
                      <div className="p-2 sm:p-2.5 bg-white border border-gray-300 rounded text-sm sm:text-base text-gray-900 break-words">
                        {(() => {
                          try {
                            const date = new Date(submission.expiryDate);
                            if (isNaN(date.getTime())) {
                              return submission.expiryDate;
                            }
                            return date.toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            });
                          } catch {
                            return submission.expiryDate;
                          }
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Container - Images and KYC Status */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6">
              <DocumentPreview
                src={submission.businessLicenceImageUrl}
                alt="Business Licence"
                label="Business Licence Document"
              />

              <DocumentPreview
                src={submission.businessLicenseRegistrationImageUrl}
                alt="Business License Registration"
                label="Business License Registration Document"
              />

              <DocumentPreview
                src={submission.idPassportImageUrl}
                alt="ID/Passport"
                label="ID/Passport Document"
              />

              {/* KYC Verification Status - At the end */}
              <div className="mt-4 sm:mt-6">
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  KYC Verification Status
                </label>
                <select
                  value={verificationStatus}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="w-full md:w-1/2 p-2.5 sm:p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                >
                  <option value="pending">pending</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                  <option value="expired">expired</option>
                </select>

                {/* Rejection Reason - Dynamic text box */}
                {verificationStatus === "rejected" && (
                  <div className="mt-3 sm:mt-4">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      Rejection Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Please provide a reason for rejecting this KYC submission..."
                      className="w-full md:w-1/2 p-2.5 sm:p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base resize-y min-h-[80px] sm:min-h-[100px]"
                      rows="4"
                      required
                    />
                    <p className="mt-1 text-xs sm:text-sm text-gray-500">
                      This reason will be visible to the user.
                    </p>
                  </div>
                )}

                {/* Expiry Date - only for approved */}
                {verificationStatus === "approved" && (
                  <div className="mt-3 sm:mt-4">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      Expiry Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      className="w-full md:w-1/2 p-2.5 sm:p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                      required
                    />
                    <p className="mt-1 text-xs sm:text-sm text-gray-500">
                      Set the business license expiration date. This date indicates when the KYC verification expires.
                    </p>
                    {submission.expiryDate && (
                      <p className="mt-1 text-xs sm:text-sm text-blue-600">
                        Current expiry date: {(() => {
                          try {
                            const date = new Date(submission.expiryDate);
                            if (isNaN(date.getTime())) {
                              return submission.expiryDate;
                            }
                            return date.toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            });
                          } catch {
                            return submission.expiryDate;
                          }
                        })()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-300">
              <button
                onClick={onClose}
                className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors text-sm sm:text-base font-medium"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base font-medium"
                disabled={
                  saving ||
                  (verificationStatus === "approved" && !expiryDate) ||
                  (verificationStatus === "rejected" &&
                    !rejectionReason.trim()) ||
                  (verificationStatus === submission.kycVerificationStatus &&
                    rejectionReason === (submission.rejectionReason || "") &&
                    (!expiryDate || expiryDate === (submission.expiryDate 
                      ? (submission.expiryDate.includes('T') 
                          ? submission.expiryDate.split('T')[0] 
                          : submission.expiryDate.split(' ')[0])
                      : "")))
                }
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal for Full Size View */}
      {showImageModal && (
        <div
          className="fixed inset-0 bg-black/90 flex justify-center items-center z-[60] p-2 sm:p-4"
          onClick={() => setShowImageModal(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full">
            <button
              onClick={() => setShowImageModal(null)}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white text-3xl sm:text-4xl hover:text-gray-300 z-10 bg-black/50 rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center"
              aria-label="Close"
            >
              ×
            </button>
            <img
              src={showImageModal}
              alt="Full size preview"
              className="max-w-full max-h-[90vh] object-contain mx-auto"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default KYCEditModal;

